import { useCallback, useEffect } from "react";

import { useTaskSidebarStore } from "@/features/task-progress/store/task-sidebar.store";

/** Long enough that a cursor crossing the header chip does not fling the panel open. */
export const TASK_SIDEBAR_HOVER_OPEN_DELAY_MS = 100;

/** Covers the gap between the header chip and the panel's top edge. */
export const TASK_SIDEBAR_HOVER_CLOSE_DELAY_MS = 250;

// Module-scoped: the chip and the panel are separate components, so moving the
// pointer from one to the other has to cancel the timer the other one started.
let openTimer: ReturnType<typeof setTimeout> | null = null;
let closeTimer: ReturnType<typeof setTimeout> | null = null;

function clearOpenTimer() {
  if (openTimer) clearTimeout(openTimer);
  openTimer = null;
}

function clearCloseTimer() {
  if (closeTimer) clearTimeout(closeTimer);
  closeTimer = null;
}

/**
 * Hover-open and hover-close for the tasks panel, mirroring the tenant switcher
 * in `TopNav`. Attach both handlers to the header chip and to the panel so
 * transit between them never collapses it.
 */
export function useTaskSidebarHover() {
  const handleHoverOpen = useCallback(() => {
    clearCloseTimer();
    if (useTaskSidebarStore.getState().open || openTimer) return;
    openTimer = setTimeout(() => {
      openTimer = null;
      useTaskSidebarStore.getState().expand();
    }, TASK_SIDEBAR_HOVER_OPEN_DELAY_MS);
  }, []);

  const handleHoverClose = useCallback(() => {
    clearOpenTimer();
    if (useTaskSidebarStore.getState().pinned) return;
    clearCloseTimer();
    closeTimer = setTimeout(() => {
      closeTimer = null;
      useTaskSidebarStore.getState().collapse();
    }, TASK_SIDEBAR_HOVER_CLOSE_DELAY_MS);
  }, []);

  useEffect(
    () => () => {
      clearOpenTimer();
      clearCloseTimer();
    },
    [],
  );

  return { handleHoverOpen, handleHoverClose };
}
